/**
 * SkillCandidatesPanel — Auto-Detected Skills Tab (Tier 2)
 * 
 * Shows skill candidates auto-detected by the AutoSkillDetector from conversation corrections.
 * Users can see what patterns the system has identified.
 * Integrates into CognitiveLab as a tab.
 */
import React, { useState, useEffect, useCallback } from 'react';

interface SkillCandidate {
  id: string;
  category: string;
  name: string;
  description: string;
  confidenceTier: string;
  patterns: string[];
  correctionCount: number;
  detectedAt: string;
}

interface Props {
  replicaId: string;
  getToken: () => Promise<string>;
}

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:3001';

const CATEGORY_ICONS: Record<string, string> = {
  email_drafting: '✉️',
  meeting_scheduling: '📅',
  task_prioritization: '📊',
  document_review: '📝',
  communication_style: '💬',
  content_creation: '🖊️',
  code_review: '💻',
  financial_decisions: '💰',
  negotiation: '🤝',
  data_analysis: '📈',
  custom: '⚙️',
  general: '🧩',
};

const TIER_STYLES: Record<string, { label: string; bg: string; text: string; border: string }> = {
  suggested:  { label: 'Suggested',  bg: 'bg-amber-100 dark:bg-amber-900/30',  text: 'text-amber-700 dark:text-amber-400',  border: 'border-amber-200 dark:border-amber-700/50' },
  validated:  { label: 'Validated',  bg: 'bg-green-100 dark:bg-green-900/30',   text: 'text-green-700 dark:text-green-400',   border: 'border-green-200 dark:border-green-700/50' },
  autonomous: { label: 'Autonomous', bg: 'bg-indigo-100 dark:bg-indigo-900/30', text: 'text-indigo-700 dark:text-indigo-400', border: 'border-indigo-200 dark:border-indigo-700/50' },
};

export const SkillCandidatesPanel: React.FC<Props> = ({ replicaId, getToken }) => {
  const [candidates, setCandidates] = useState<SkillCandidate[]>([]);
  const [loading, setLoading] = useState(true);
  const [detecting, setDetecting] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const loadCandidates = useCallback(async () => {
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/api/v1/skill-candidates/${replicaId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success) {
        setCandidates(data.candidates || []);
      }
    } catch (e) {
      console.error('Failed to load skill candidates:', e);
    } finally {
      setLoading(false);
    }
  }, [replicaId, getToken]);

  useEffect(() => { loadCandidates(); }, [loadCandidates]);

  const triggerDetection = async () => {
    setDetecting(true);
    try {
      const token = await getToken();
      await fetch(`${API_BASE}/api/v1/skill-candidates/${replicaId}/detect`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      await loadCandidates();
    } catch (e) {
      console.error('Skill detection failed:', e);
    } finally {
      setDetecting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="w-6 h-6 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="flex-1 p-6 overflow-auto">
      <div className="max-w-3xl mx-auto space-y-5">

        {/* ── Header ── */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="text-2xl">🔮</span>
            <div>
              <h2 className="text-lg font-bold text-slate-900 dark:text-white">Auto-Detected Skills</h2>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                HR-BREV-008 · Skills learned from conversation corrections
              </p>
            </div>
          </div>
          <button
            onClick={triggerDetection}
            disabled={detecting}
            className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 text-white text-xs font-semibold rounded-xl transition-colors"
          >
            {detecting ? (
              <span className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <span>🔍</span>
            )}
            {detecting ? 'Analyzing...' : 'Run Detection'}
          </button>
        </div>

        {/* ── Empty State ── */}
        {candidates.length === 0 && (
          <div className="bg-white/60 dark:bg-slate-800/60 backdrop-blur-sm border border-slate-200 dark:border-slate-700/50 rounded-2xl p-12 text-center">
            <span className="text-4xl block mb-3">🧠</span>
            <h3 className="text-base font-bold text-slate-900 dark:text-white mb-2">
              No skills detected yet
            </h3>
            <p className="text-sm text-slate-500 dark:text-slate-400 max-w-md mx-auto mb-4">
              Your replica learns from corrections. When you correct the same type of response 
              3+ times within a week, a skill candidate is automatically created.
            </p>
            <div className="flex items-center justify-center gap-4 text-xs text-slate-400 dark:text-slate-500">
              <span>💡 Correct responses → Patterns emerge → Skills form</span>
            </div>
          </div>
        )}

        {/* ── Candidates List ── */}
        {candidates.map(candidate => {
          const icon = CATEGORY_ICONS[candidate.category] || CATEGORY_ICONS.general;
          const tierStyle = TIER_STYLES[candidate.confidenceTier] || TIER_STYLES.suggested;
          const isExpanded = expandedId === candidate.id;

          return (
            <div
              key={candidate.id}
              className="bg-white/60 dark:bg-slate-800/60 backdrop-blur-sm border border-slate-200 dark:border-slate-700/50 rounded-2xl overflow-hidden transition-all"
            >
              {/* Card Header */}
              <button
                onClick={() => setExpandedId(isExpanded ? null : candidate.id)}
                className="w-full flex items-center justify-between p-4 hover:bg-slate-50/50 dark:hover:bg-slate-700/20 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <span className="text-xl">{icon}</span>
                  <div className="text-left">
                    <h3 className="text-sm font-bold text-slate-900 dark:text-white">
                      {candidate.name}
                    </h3>
                    <p className="text-xs text-slate-500 dark:text-slate-400">
                      {candidate.correctionCount} corrections · Detected{' '}
                      {new Date(candidate.detectedAt).toLocaleDateString()}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${tierStyle.bg} ${tierStyle.text} ${tierStyle.border}`}>
                    {tierStyle.label}
                  </span>
                  <span className="text-slate-400 text-xs">{isExpanded ? '▲' : '▼'}</span>
                </div>
              </button>

              {/* Expanded Detail */}
              {isExpanded && (
                <div className="border-t border-slate-200 dark:border-slate-700/50 p-4 space-y-3">
                  <p className="text-sm text-slate-600 dark:text-slate-300">{candidate.description}</p>

                  {candidate.patterns.length > 0 && (
                    <div>
                      <h4 className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-2">
                        Learned Patterns
                      </h4>
                      <div className="space-y-1.5">
                        {candidate.patterns.map((pattern, i) => (
                          <div
                            key={i}
                            className="flex items-start gap-2 px-3 py-2 bg-slate-50 dark:bg-slate-700/30 rounded-lg"
                          >
                            <span className="text-indigo-400 text-xs mt-0.5">#{i + 1}</span>
                            <p className="text-xs text-slate-600 dark:text-slate-300 leading-relaxed">
                              "{pattern}"
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="flex items-center gap-2 pt-2">
                    <span className="text-[10px] text-slate-400 dark:text-slate-500">
                      Category: {candidate.category} · Tier: {candidate.confidenceTier}
                    </span>
                  </div>
                </div>
              )}
            </div>
          );
        })}

        {/* ── How It Works ── */}
        <div className="bg-slate-50/80 dark:bg-slate-800/30 border border-slate-200 dark:border-slate-700/30 rounded-xl p-4">
          <h4 className="text-xs font-semibold text-slate-500 dark:text-slate-400 mb-2">How Auto-Skill Detection Works</h4>
          <div className="grid grid-cols-3 gap-3 text-center">
            {[
              { step: '1', icon: '🔄', label: 'You correct responses', desc: 'Natural conversation corrections' },
              { step: '2', icon: '📊', label: 'Patterns cluster', desc: '3+ corrections in same domain' },
              { step: '3', icon: '✨', label: 'Skill is born', desc: 'Suggested tier, grows with use' },
            ].map(s => (
              <div key={s.step} className="space-y-1">
                <span className="text-xl block">{s.icon}</span>
                <p className="text-xs font-semibold text-slate-700 dark:text-slate-300">{s.label}</p>
                <p className="text-[10px] text-slate-400 dark:text-slate-500">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SkillCandidatesPanel;
